
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  History, 
  FileSpreadsheet, 
  LogOut,
  Plus,
  Settings,
  Users,
  NotebookPen
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import Logo from './Logo';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { label: 'Overview', icon: LayoutDashboard, path: '/' },
    { label: 'History', icon: History, path: '/history' },
    { label: 'Groups', icon: Users, path: '/groups' },
    { label: 'Notes', icon: NotebookPen, path: '/notes' },
    { label: 'Reports', icon: FileSpreadsheet, path: '/export' },
    { label: 'Settings', icon: Settings, path: '/settings' },
  ];

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const showFAB = !location.pathname.startsWith('/add') && !location.pathname.startsWith('/edit');

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 pb-24 md:pb-0 md:pl-72">
      <aside className="hidden md:flex flex-col fixed left-0 top-0 bottom-0 w-72 bg-white border-r border-slate-100 z-30 shadow-2xl shadow-slate-200/20">
        <div className="p-12 flex flex-col items-center border-b border-slate-50">
          <Logo size={64} className="mb-4" />
          <div className="text-center">
            <h1 className="text-xl font-black text-[#0f172a] leading-none tracking-tight">SECURETRACK</h1>
            <span className="text-[9px] font-black text-slate-300 uppercase tracking-[0.4em] mt-1 block">Simple Financials</span>
          </div>
        </div>
        <nav className="flex-1 px-8 space-y-4 pt-10">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex items-center w-full px-6 py-4 rounded-[1.8rem] transition-all duration-300 ${
                location.pathname === item.path 
                  ? 'bg-[#0f172a] text-[#fbbf24] shadow-xl shadow-slate-200 translate-x-1' 
                  : 'text-slate-400 hover:text-[#0f172a] hover:bg-slate-50'
              }`}
            >
              <item.icon className={`w-5 h-5 mr-4 ${location.pathname === item.path ? 'opacity-100' : 'opacity-50'}`} />
              <span className="font-extrabold text-sm tracking-tight">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-8 border-t border-slate-50">
          <button 
            onClick={handleLogout}
            className="flex items-center w-full px-8 py-4 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-[1.8rem] transition-all font-black text-[10px] uppercase tracking-widest"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-14">
        <div className="max-w-5xl mx-auto">
          {children}
        </div>
      </main>

      {showFAB && (
        <button 
          onClick={() => navigate('/add')}
          className="fixed right-6 bottom-32 md:right-12 md:bottom-12 bg-[#0f172a] text-[#fbbf24] w-20 h-20 md:w-24 md:h-24 rounded-[2rem] md:rounded-[2.5rem] shadow-[0_25px_50px_-12px_rgba(15,23,42,0.4)] z-50 active:scale-90 hover:scale-105 transition-all flex items-center justify-center border-4 border-white/5 group"
          aria-label="Add Record"
        >
          <div className="absolute inset-0 rounded-[inherit] bg-[#fbbf24]/5 scale-0 group-hover:scale-100 transition-transform duration-500 blur-xl"></div>
          <Plus className="w-10 h-10 md:w-12 md:h-12 relative z-10" strokeWidth={3} />
        </button>
      )}

      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-3xl border-t border-slate-100 flex justify-around items-center h-28 px-4 z-40 rounded-t-[3.5rem] shadow-[0_-20px_40px_-15px_rgba(0,0,0,0.05)]">
        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`flex flex-col items-center justify-center flex-1 transition-all ${
              location.pathname === item.path ? 'text-[#0f172a] scale-110' : 'text-slate-300'
            }`}
          >
            <item.icon className={`w-6 h-6 mb-2 ${location.pathname === item.path ? 'fill-[#fbbf24]' : ''}`} />
            <span className="text-[8px] font-black uppercase tracking-widest">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default Layout;
